import React from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import {
  getAppointmentCashSales,
  getAppointmentCreditSales,
  getAppointmentTableData,
  getAppointmentTotalSales,
  getOrderTotalSales,
  getOrderCashSales,
  getOrderCreditSales,
  getOrderTableData,
  getAppointmentTips,
  getAppointmentSalesTax,
  getOrderSalesTax,
  getOrderTips,
  getAppointmentGiftCardSales,
  getOrderGiftCardSales,
} from '../../helpers/reports';
import {
  Column,
  DataFromColumns,
  DateType,
  ReportAppointment,
  ReportOrder,
  TableType,
} from '../../types/reports';
import {
  APPT_TABLE_COLUMNS,
  ORDER_TABLE_COLUMNS,
} from '../../constants/reports';
import { toCsv } from '../../utils/global/csv';
import { DataFromHeaders } from '../../types/csv';

type AppointmentColumnData = Column<
  (typeof APPT_TABLE_COLUMNS)[number]['dataKey']
>;

type OrderColumnData = Column<(typeof ORDER_TABLE_COLUMNS)[number]['dataKey']>;

export type AppointmentTableData = DataFromColumns<
  Array<AppointmentColumnData>
>[];

export type OrderTableData = DataFromColumns<Array<OrderColumnData>>[];

function useTransformReports({
  appointments,
  orders,
  dateType,
  startDate,
  endDate,
  selectedProducts,
}: {
  appointments: ReportAppointment[];
  orders: ReportOrder[];
  dateType: DateType;
  startDate: Date | null;
  endDate: Date | null;
  selectedProducts: string[];
}) {
  // Get total sales EXCLUDING tips and taxes. This number should be used for the Revenue Report total sales.
  const totalSales = React.useMemo(
    () =>
      appointments.reduce(
        (currentTotal, appointment) =>
          currentTotal +
          getAppointmentTotalSales({
            appointment,
            dateType,
            startDate,
            endDate,
            includeTips: false,
            includeTaxes: false,
            includeNonSuccess: false,
          }),
        0,
      ) +
      orders.reduce(
        (currentTotal, order) =>
          currentTotal +
          getOrderTotalSales({
            order,
            includeTaxes: false,
            includeTips: false,
            includeNonSuccess: false,
            selectedProducts,
          }),
        0,
      ),
    [appointments, orders, dateType, endDate, startDate, selectedProducts],
  );

  // Get credit sales EXCLUDING tips and taxes. This number should be used for the Revenue Report credit sales.
  const creditSales = React.useMemo(
    () =>
      appointments.reduce(
        (currentTotal, appointment) =>
          currentTotal +
          getAppointmentCreditSales({
            includeTips: false,
            appointment,
            includeTaxes: false,
            includeNonSuccess: false,
          }),
        0,
      ) +
      orders.reduce(
        (currentTotal, order) =>
          currentTotal +
          getOrderCreditSales({
            order,
            includeTips: false,
            includeTaxes: false,
            includeNonSuccess: false,
            selectedProducts,
          }),
        0,
      ),
    [appointments, orders, selectedProducts],
  );

  // Get cash sales EXCLUDING tips and taxes. This number should be used for the Revenue Report cash sales.
  const cashSales = React.useMemo(
    () =>
      appointments.reduce(
        (currentTotal, appointment) =>
          currentTotal +
          getAppointmentCashSales({
            includeTaxes: false,
            includeTips: false,
            includeNonSuccess: false,
            appointment,
            startDate,
            endDate,
            dateType,
          }),
        0,
      ) +
      orders.reduce(
        (currentTotal, order) =>
          currentTotal +
          getOrderCashSales({
            order,
            includeTaxes: false,
            includeTips: false,
            includeNonSuccess: false,
            selectedProducts,
          }),
        0,
      ),
    [appointments, orders, startDate, endDate, dateType, selectedProducts],
  );

  // Get gift card sales EXCLUDING tips and taxes. This number should be used for the Revenue Report gift card sales.
  const giftCardSales = React.useMemo(
    () =>
      appointments.reduce(
        (currentTotal, appointment) =>
          currentTotal +
          getAppointmentGiftCardSales({
            appointment,
            includeTaxes: false,
            includeTips: false,
            includeNonSuccess: false,
          }),
        0,
      ) +
      orders.reduce(
        (currentTotal, order) =>
          currentTotal +
          getOrderGiftCardSales({
            order,
            includeTaxes: false,
            includeTips: false,
            includeNonSuccess: false,
            selectedProducts,
          }),
        0,
      ),
    [appointments, orders, selectedProducts],
  );

  // Get appointment table data always including tips and taxes
  const appointmentsTableData = React.useMemo<AppointmentTableData>(
    () =>
      appointments.map(appointment =>
        getAppointmentTableData({
          dateType,
          endDate,
          includeTips: true,
          includeTaxes: true,
          appointment,
          startDate,
        }),
      ),
    [appointments, dateType, endDate, startDate],
  );

  // Get order table data always including tips and taxes
  const ordersTableData = React.useMemo<OrderTableData>(
    () =>
      orders.map(order =>
        getOrderTableData({
          order,
          includeTaxes: true,
          includeTips: true,
          selectedProducts,
        }),
      ),
    [orders, selectedProducts],
  );

  // Get appointment tips for tips report
  const appointmentTips = React.useMemo(
    () =>
      appointments.reduce(
        (acc, appointment) => {
          const { cash, credit, total, giftCard } = getAppointmentTips({
            appointment,
            startDate,
            endDate,
            dateType,
            includeNonSuccess: false,
          });

          return {
            cash: acc.cash + cash,
            credit: acc.credit + credit,
            giftCard: acc.giftCard + giftCard,
            total: acc.total + total,
          };
        },
        { cash: 0, credit: 0, total: 0, giftCard: 0 },
      ),
    [appointments, dateType, endDate, startDate],
  );

  // Get appointment tips for tips report
  const orderTips = React.useMemo(
    () =>
      orders.reduce(
        (acc, order) => {
          const { cash, credit, giftCard, total } = getOrderTips({
            order,
            selectedProducts,
            includeNonSuccess: false,
          });

          return {
            cash: acc.cash + cash,
            credit: acc.credit + credit,
            giftCard: acc.giftCard + giftCard,
            total: acc.total + total,
          };
        },
        { cash: 0, credit: 0, giftCard: 0, total: 0 }, // Initial values
      ),
    [orders, selectedProducts],
  );

  const totalTips = React.useMemo(
    () => ({
      cash: appointmentTips.cash + orderTips.cash,
      credit: appointmentTips.credit + orderTips.credit,
      giftCard: appointmentTips.giftCard + orderTips.giftCard,
      total: appointmentTips.total + orderTips.total,
    }),
    [appointmentTips, orderTips],
  );

  const appointmentSalesTax = React.useMemo(
    () =>
      appointments.reduce(
        (acc, appointment) => {
          const { cash, credit, giftCard, total } = getAppointmentSalesTax({
            appointment,
            startDate,
            endDate,
            dateType,
            includeNonSuccess: false,
          });

          return {
            cash: acc.cash + cash,
            credit: acc.credit + credit,
            giftCard: acc.giftCard + giftCard,
            total: acc.total + total,
          };
        },
        { cash: 0, credit: 0, giftCard: 0, total: 0 }, // Initial values
      ),
    [appointments, dateType, endDate, startDate],
  );

  const orderSalesTax = React.useMemo(
    () =>
      orders.reduce(
        (acc, order) => {
          const { cash, credit, giftCard, total } = getOrderSalesTax({
            order,
            selectedProducts,
            includeNonSuccess: false,
          });

          return {
            cash: acc.cash + cash,
            credit: acc.credit + credit,
            giftCard: acc.giftCard + giftCard,
            total: acc.total + total,
          };
        },
        { cash: 0, credit: 0, giftCard: 0, total: 0 },
      ),
    [orders, selectedProducts],
  );

  const salesTax = React.useMemo(
    () => ({
      cash: appointmentSalesTax.cash + orderSalesTax.cash,
      credit: appointmentSalesTax.credit + orderSalesTax.credit,
      giftCard: appointmentSalesTax.giftCard + orderSalesTax.giftCard,
      total: appointmentSalesTax.total + orderSalesTax.total,
    }),
    [appointmentSalesTax, orderSalesTax],
  );

  const downloadCsv = React.useCallback(
    <TType extends TableType>(table: TType) => {
      try {
        const isAppointmentTable = table === 'appointment';

        const tableData = isAppointmentTable
          ? appointmentsTableData
          : ordersTableData;

        const columns = (
          isAppointmentTable
            ? APPT_TABLE_COLUMNS.filter(
                (column: AppointmentColumnData) => !column.hidden,
              )
            : ORDER_TABLE_COLUMNS.filter(
                (column: OrderColumnData) => !column.hidden,
              )
        ) as TType extends 'appointment'
          ? Array<AppointmentColumnData>
          : Array<OrderColumnData>;

        const data = tableData.map(
          row =>
            row as DataFromHeaders<
              TType extends 'appointment'
                ? AppointmentColumnData[]
                : OrderColumnData[]
            >,
        );

        if (!data.length) {
          toast.warning('No data to download');
          return;
        }

        const startDateName = startDate
          ? new Date(startDate).toDateString().replace(/[^0-9]/g, '')
          : '';
        const endDateName = endDate
          ? new Date(endDate).toDateString().replace(/[^0-9]/g, '')
          : '';
        const fileName = `porter${
          !isAppointmentTable ? '-retail' : ''
        }-${startDateName}-${endDateName}`;

        const titleCell = `Title: ${
          isAppointmentTable
            ? 'Porter Appointment Transactions'
            : 'Porter Retail Checkouts'
        }`;

        const startDateString = startDate
          ? format(new Date(startDate), 'MM/dd/yyyy')
          : '';
        const endDateString = endDate
          ? format(new Date(endDate), 'MM/dd/yyyy')
          : '';

        const dateCell = `Date Range: ${startDateString} - ${endDateString}`;

        const reportTitleRows = `${titleCell}\n${dateCell}\n`;

        toCsv<
          TType extends 'appointment'
            ? Array<AppointmentColumnData>
            : Array<OrderColumnData>
        >({
          columns,
          data,
          fileName,
          reportTitleRows,
        });
      } catch {
        toast.error('Error downloading CSV');
      }
    },
    [appointmentsTableData, ordersTableData, endDate, startDate],
  );

  return {
    data: {
      appointmentCount: appointments.length,
      totalSales: Math.max(0, totalSales),
      cashSales: Math.max(0, cashSales),
      creditSales: Math.max(0, creditSales),
      giftCardSales: Math.max(0, giftCardSales),
      totalTips,
      salesTax,
      appointmentsTableData,
      ordersTableData,
    },
    downloadCsv,
  };
}

export default useTransformReports;
